import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { users as usersApi } from '../api/client.js';
import UserRow from './users/UserRow.jsx';
import UserDetailPanel from './users/UserDetailPanel.jsx';
import InviteModal from './users/InviteModal.jsx';
import EditUserModal from './users/EditUserModal.jsx';
import ResetPasswordModal from './users/ResetPasswordModal.jsx';

export default function UserManagement() {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);

  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const [statusMessage, setStatusMessage] = useState('');

  const fetchUsers = useCallback(() => {
    setLoading(true);
    usersApi.list(isAdmin && showInactive ? { all: true } : {})
      .then(data => setUsers(data.users || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isAdmin, showInactive]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedUserDetail(null);
      return;
    }
    setActiveTab('overview');
    setDetailLoading(true);
    usersApi.get(selectedUserId)
      .then(data => setSelectedUserDetail(data))
      .catch(console.error)
      .finally(() => setDetailLoading(false));
  }, [selectedUserId]);

  useEffect(() => {
    if (!selectedUserId || activeTab !== 'activity') return;
    setActivityLoading(true);
    usersApi.activity(selectedUserId)
      .then(data => setActivityLog(data.activities || []))
      .catch(console.error)
      .finally(() => setActivityLoading(false));
  }, [selectedUserId, activeTab]);

  const showStatus = (msg) => {
    setStatusMessage(msg);
    setTimeout(() => setStatusMessage(''), 3000);
  };

  const handleInvite = async ({ email, name, role }) => {
    await usersApi.invite({ email, name, role });
    showStatus(`Invited ${email}`);
    fetchUsers();
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersApi.updateRole(userId, newRole);
      showStatus('Role updated');
      fetchUsers();
      if (selectedUserId === userId) {
        usersApi.get(userId).then(setSelectedUserDetail).catch(console.error);
      }
    } catch (err) {
      alert(err.data?.error || 'Failed to update role');
    }
  };

  const handleDeactivate = async (userId, userName) => {
    if (!confirm(`Deactivate ${userName}? They will no longer be able to log in.`)) return;
    try {
      await usersApi.deactivate(userId);
      showStatus(`${userName} deactivated`);
      fetchUsers();
      if (selectedUserId === userId) setSelectedUserId(null);
    } catch (err) {
      alert(err.data?.error || 'Failed to deactivate user');
    }
  };

  const handleReactivate = async (userId, userName) => {
    try {
      await usersApi.reactivate(userId);
      showStatus(`${userName} reactivated`);
      fetchUsers();
    } catch (err) {
      alert(err.data?.error || 'Failed to reactivate user');
    }
  };

  const handleEditSave = async (userId, { name, initials, color }) => {
    await usersApi.update(userId, { name, initials, color });
    showStatus('User updated');
    fetchUsers();
    if (selectedUserId === userId) {
      usersApi.get(userId).then(setSelectedUserDetail).catch(console.error);
    }
  };

  const handleResetPassword = async (userId, password) => {
    await usersApi.resetPassword(userId, password);
    showStatus('Password reset');
  };

  const filteredUsers = users.filter(u => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    }
    return true;
  });

  const activeUsers = filteredUsers.filter(u => u.is_active);
  const inactiveUsers = filteredUsers.filter(u => !u.is_active);
  const selectedDetail = selectedUserDetail?.user;

  return (
    <div style={{ padding: '28px 32px', flex: 1, overflowY: 'auto' }}>
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 24, borderBottom: `2px solid ${theme.text}`, paddingBottom: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.textFaint }}>team · access</div>
          <h2 style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, fontSize: 34, lineHeight: 1.1, color: theme.text, margin: '8px 0 0', letterSpacing: '0.01em' }}>
            Who can reach the vault
          </h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {statusMessage && (
            <span style={{ fontSize: 12, color: theme.accent }}>{statusMessage}</span>
          )}
          <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.textFaint }}>
            {activeUsers.length} active{inactiveUsers.length > 0 ? ` · ${inactiveUsers.length} inactive` : ''}
          </span>
          {isAdmin && inactiveUsers.length > 0 && (
            <span onClick={() => setShowInactive(!showInactive)} style={{ fontSize: 14, color: theme.accent, cursor: 'pointer', textTransform: 'lowercase' }}>
              {showInactive ? 'hide inactive.' : 'show inactive.'}
            </span>
          )}
          {isAdmin && (
            <span onClick={() => setShowInvite(true)} style={{ fontSize: 14, color: theme.accent, cursor: 'pointer', textTransform: 'lowercase' }}>
              invite someone.
            </span>
          )}
        </div>
      </div>

      <div style={{ margin: '20px 0 0', maxWidth: 420 }}>
        <input
          type="text"
          placeholder="search by name or email"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            width: '100%', height: 40, padding: '6px 14px',
            border: `1px solid ${theme.borderMedium}`, background: 'transparent',
            fontFamily: 'inherit', fontSize: 13, color: theme.text, outline: 'none', borderRadius: 0,
          }}
        />
      </div>

      <div style={{ marginTop: 24, borderTop: `1px solid ${theme.border}` }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint, fontSize: 12 }}>loading users…</div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint, fontSize: 12 }}>no users found</div>
        ) : (
          <>
            {activeUsers.map(u => (
              <UserRow
                key={u.id}
                user={u}
                isCurrent={currentUser?.id === u.id}
                isAdmin={isAdmin}
                theme={theme}
                onClick={() => setSelectedUserId(u.id)}
              />
            ))}
            {showInactive && inactiveUsers.map(u => (
              <UserRow
                key={u.id}
                user={u}
                isCurrent={false}
                isAdmin={isAdmin}
                theme={theme}
                isInactive
                onClick={() => setSelectedUserId(u.id)}
                onReactivate={() => handleReactivate(u.id, u.name)}
              />
            ))}
          </>
        )}
      </div>

      {selectedUserId && (
        <div style={{
          position: 'fixed', top: 76, right: 0, bottom: 0,
          zIndex: 90, display: 'flex', flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        }}>
          {detailLoading ? (
            <aside style={{
              width: 360, borderLeft: `2px solid ${theme.text}`, padding: 20,
              background: theme.bgElevated, height: '100%', boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: theme.textFaint, fontSize: 12 }}>loading…</span>
            </aside>
          ) : (
            <UserDetailPanel
              selectedDetail={selectedDetail}
              currentUser={currentUser}
              isAdmin={isAdmin}
              theme={theme}
              onClose={() => setSelectedUserId(null)}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              activityLog={activityLog}
              activityLoading={activityLoading}
              onRoleChange={handleRoleChange}
              onDeactivate={handleDeactivate}
              onReactivate={handleReactivate}
              onEdit={(user) => setEditingUser(user)}
              onResetPassword={(userId) => setResetPasswordUserId(userId)}
            />
          )}
        </div>
      )}

      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onInvite={handleInvite} />
      )}

      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onSave={handleEditSave} />
      )}

      {resetPasswordUserId && (
        <ResetPasswordModal userId={resetPasswordUserId} onClose={() => setResetPasswordUserId(null)} onReset={handleResetPassword} />
      )}
    </div>
  );
}
